import { COMPLETION_FRACTION, CONTINUE_MIN_POSITION_SEC } from '@anisync/types';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { beaconProgress, getTitleProgress, reportProgress } from '../lib/api';

/**
 * Raportowanie postępu i wznawianie odtwarzania.
 *
 * Kadencja jest kompromisem: co 15 s w trakcie odtwarzania plus zdarzenia,
 * które zmieniają pozycję w sposób nieprzewidywalny (pauza, przewinięcie,
 * koniec, ukrycie karty). Sam interwał gubiłby do 15 s przy zamknięciu karty,
 * a same zdarzenia nie zapisałyby niczego przy nieprzerwanym oglądaniu.
 */

const REPORT_INTERVAL_MS = 15_000;

export interface PlaybackProgressOptions {
  videoRef: RefObject<HTMLVideoElement | null>;
  titleId: string | null;
  episodeNumber: number | null;
  /** Wyłączone dla gości — postęp nie ma do kogo należeć. */
  enabled: boolean;
  /** Gotowość materiału; przed nią element <video> nie istnieje. */
  ready: boolean;
}

export interface PlaybackProgressState {
  /** Sekunda, od której wznowiono — do pokazania komunikatu. */
  resumedFrom: number | null;
}

export function usePlaybackProgress({
  videoRef,
  titleId,
  episodeNumber,
  enabled,
  ready,
}: PlaybackProgressOptions): PlaybackProgressState {
  const [resumedFrom, setResumedFrom] = useState<number | null>(null);

  // Pozycja do wznowienia trzymana w refie, bo czyta ją nasłuch zdarzenia
  // `loadedmetadata`, a jego rejestracja nie może zależeć od tej wartości —
  // przy zależności nasłuch przepinałby się po każdym pobraniu postępu.
  const resumeAtRef = useRef<number | null>(null);
  const appliedResumeRef = useRef(false);

  const active = enabled && titleId !== null && episodeNumber !== null;

  // Zapisana pozycja. Zerowanie stanu wznowienia siedzi w tym samym efekcie,
  // bo dotyczy dokładnie tej samej zmiany — innego odcinka albo innego tytułu.
  useEffect(() => {
    appliedResumeRef.current = false;
    resumeAtRef.current = null;
    setResumedFrom(null);

    if (!active || titleId === null || episodeNumber === null) return;
    let cancelled = false;

    getTitleProgress(titleId)
      .then((data) => {
        if (cancelled) return;
        const entry = data.episodes.find((e) => e.episodeNumber === episodeNumber);
        if (entry === undefined || entry.completed) return;
        if (entry.positionSec < CONTINUE_MIN_POSITION_SEC) return;
        resumeAtRef.current = entry.positionSec;
      })
      // Brak zapisanego postępu nie jest błędem — odtwarzamy od początku.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [active, titleId, episodeNumber]);

  useEffect(() => {
    if (!active || !ready || titleId === null || episodeNumber === null) return;
    const video = videoRef.current;
    if (video === null) return;

    const send = (useBeacon: boolean): void => {
      const positionSec = video.currentTime;
      const durationSec = Number.isFinite(video.duration) ? video.duration : 0;
      // Zerowy czas trwania znaczy, że metadane jeszcze nie doszły; taki zapis
      // wyliczyłby ukończenie z dzielenia przez zero i zafałszował bibliotekę.
      if (durationSec <= 0) return;

      const report = { titleId, episodeNumber, positionSec, durationSec };
      if (useBeacon) {
        beaconProgress(report);
        return;
      }
      void reportProgress(report).catch(() => undefined);
    };

    const applyResume = (): void => {
      if (appliedResumeRef.current) return;
      const target = resumeAtRef.current;
      if (target === null) return;
      const duration = video.duration;
      // Nie wznawiamy w napisach końcowych — tam „kontynuuj" nie ma sensu.
      if (Number.isFinite(duration) && target >= duration * COMPLETION_FRACTION) return;

      appliedResumeRef.current = true;
      video.currentTime = target;
      setResumedFrom(target);
    };

    const onLoadedMetadata = () => applyResume();
    const onPause = () => send(false);
    const onSeeked = () => send(false);
    const onEnded = () => send(false);
    const onPageHide = () => send(true);
    const onVisibility = () => {
      // Na urządzeniach mobilnych `pagehide` często nie dochodzi, a ukrycie karty
      // jest ostatnim momentem, w którym da się cokolwiek zapisać.
      if (document.visibilityState === 'hidden') send(true);
    };

    // Metadane mogą już być dostępne, gdy element powstał przed tym efektem —
    // wtedy zdarzenie `loadedmetadata` nie nadejdzie po raz drugi.
    if (video.readyState >= 1) applyResume();

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('pause', onPause);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('ended', onEnded);
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibility);

    const interval = setInterval(() => {
      if (!video.paused && !video.ended) send(false);
    }, REPORT_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('ended', onEnded);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
      // Zapis przy odmontowaniu: przejście na inny ekran w SPA nie wywołuje
      // żadnego ze zdarzeń przeglądarki, więc bez tego pozycja by przepadła.
      send(false);
    };
  }, [active, ready, titleId, episodeNumber, videoRef]);

  return { resumedFrom };
}
